import { toast } from '@estac1/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { ApiError, api } from '../../lib/api.js';
import type { ToggleableModuleKey } from './lot-module-toggles.js';
import { formatDate } from './utils.js';

const PATH_BY_KEY: Record<ToggleableModuleKey, string> = {
  damage_photos: 'damage-photos',
  barrier: 'barrier',
  printer: 'printer',
};

export const useClientMutations = (
  setExtending: Dispatch<SetStateAction<{ lotId: string; lotName: string } | null>>,
) => {
  const qc = useQueryClient();

  const extendTrial = useMutation({
    mutationFn: (input: { lotId: string; extraDays: number }) =>
      api.request<{ trialEndsAt: string }>(`/v1/admin/clients/${input.lotId}/extend-trial`, {
        method: 'POST',
        json: { extraDays: input.extraDays },
      }),
    onSuccess: (data) => {
      toast.success(`Trial estendido até ${formatDate(data.trialEndsAt)}.`);
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
      setExtending(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : 'Falha ao estender trial.';
      toast.error(msg);
    },
  });

  const archive = useMutation({
    mutationFn: (lotId: string) =>
      api.request<{ archivedAt: string }>(`/v1/admin/clients/${lotId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Estacionamento arquivado.');
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: () => toast.error('Falha ao arquivar.'),
  });

  const unarchive = useMutation({
    mutationFn: (lotId: string) =>
      api.request<{ archivedAt: null }>(`/v1/admin/clients/${lotId}/unarchive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Estacionamento desarquivado.');
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: () => toast.error('Falha ao desarquivar.'),
  });

  const resetCheckout = useMutation({
    mutationFn: (lotId: string) =>
      api.request<{ ok: true; providerRemoved: boolean | null; providerError: string | null }>(
        `/v1/admin/clients/${lotId}/reset-checkout`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      if (data.providerRemoved === false) {
        toast.error(
          `Checkout resetado, mas a assinatura não saiu do Asaas: ${data.providerError ?? 'erro desconhecido'}. Remova por lá.`,
        );
      } else {
        toast.success('Checkout resetado. O dono pode iniciar um novo plano.');
      }
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: () => toast.error('Falha ao resetar checkout.'),
  });

  const toggleModule = useMutation({
    mutationFn: (input: { lotId: string; key: ToggleableModuleKey; enabled: boolean }) =>
      api.request<Record<string, boolean>>(
        `/v1/admin/clients/${input.lotId}/${PATH_BY_KEY[input.key]}`,
        {
          method: 'PATCH',
          json: { enabled: input.enabled },
        },
      ),
    onSuccess: (_, vars) => {
      toast.success(vars.enabled ? 'Módulo ativado.' : 'Módulo desativado.');
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao atualizar módulo.');
    },
  });

  return { extendTrial, archive, unarchive, resetCheckout, toggleModule };
};
